import { LogOut } from 'lucide-react';
import { useMe, useLogout, goToLogin, AuthError } from '@/lib/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function initials(nameOrEmail: string): string {
  return nameOrEmail
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function UserMenu() {
  const { data, isLoading, error } = useMe();
  const logout = useLogout();

  if (isLoading) {
    // Reserve the avatar slot so the header doesn't reflow when /me resolves.
    return <div className="size-8 shrink-0 rounded-full bg-muted/40" aria-hidden />;
  }

  if (error instanceof AuthError || !data) {
    return (
      <Button variant="outline" size="sm" onClick={goToLogin}>
        Log in
      </Button>
    );
  }

  const u = data.user;
  const label = u.name || u.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open account menu"
          // Trigger is just the avatar — keeps the top bar narrow on phones
          // where the email used to push the header past the viewport.
          className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Avatar className="size-8">
            {u.picture && <AvatarImage src={u.picture} alt={label} />}
            <AvatarFallback className="text-[10px] font-medium">
              {initials(label)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate text-sm font-medium leading-tight">{u.name || u.email}</div>
          {u.name && (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{u.email}</div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout.mutate()} className="gap-2">
          <LogOut className="size-4" strokeWidth={1.75} />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
